import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import classNames from 'classnames';

import { get, csrf } from '../../services/api';
import { Status, TrackingEvent } from '../../types';
import styles from './Tracking.module.scss';
import { getOverlayContainer, enterOverlayLayer, exitOverlayLayer } from '../../utils/shadowMount';

export function Tracking() {
  const codeParam = new URLSearchParams(document.location.search).get('code');
  const [codeValue, setCodeValue] = useState<string>(codeParam ?? '');
  const [events, setEvents] = useState<TrackingEvent[]>([]);
  const [status, setStatus] = useState<Status>('idle');
  const [requested, setRequested] = useState(false);
  const [masterStatus, setMasterStatus] = useState<string | null>('');
  const [consignmentCode, setConsignmentCode] = useState<string | null>('');
  const [packageIds, setPackageIds] = useState<string[]>([]);
  const [courierWebsite, setCourierWebsite] = useState<string | null>('');
  const [courierName, setCourierName] = useState<string | null>('');
  // Controls the results popup. Opened when a lookup finishes (complete/error),
  // dismissible by the user without re-triggering until the next lookup.
  const [showResults, setShowResults] = useState(false);

  // Runs the tracking lookup. Called directly on each Check click (and once on
  // mount if a ?code= URL param is present) so re-checking the SAME code still
  // works — tying this only to a `code` state change meant identical values
  // were skipped by React and nothing happened.
  const runLookup = async (trackingCode: string) => {
    if (trackingCode === '') {
      return;
    }
    setEvents([]);
    setStatus('in-progress');
    setShowResults(false);
    try {
      await csrf();
      const { data }: any = await get(`/tracking?code=${trackingCode}`);

      setEvents(data.events);
      setConsignmentCode(data.item);
      setPackageIds(Array.isArray(data.package_ids) ? data.package_ids : []);

      const events = data.events;

      // Check if there are events and if the last event has the master_status parameter
      if (events.length > 0 && events[events.length - 1].hasOwnProperty('master_status')) {
        setMasterStatus(events[events.length - 1].master_status ? events[events.length - 1].master_status : 'On its Way');
      }

      if (events.length > 0) {
        setCourierWebsite(events[0].website_link);
        setCourierName(events[0].courier);
      }
      setStatus('complete');
      setShowResults(true);
    } catch (e) {
      setStatus('error');
      setShowResults(true);
    }
  };

  // Run once on mount if a tracking code was supplied via the URL.
  useEffect(() => {
    if (codeParam) {
      runLookup(codeParam.trim());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loading = status === 'in-progress';

  // Whether the results modal is currently visible.
  const modalOpen = showResults && (status === 'complete' || status === 'error');

  // While the modal is open, promote the shadow host to a full-viewport layer
  // so the popup sits above the WordPress header; restore it on close/unmount.
  useEffect(() => {
    if (modalOpen) {
      enterOverlayLayer();
    } else {
      exitOverlayLayer();
    }
    return () => exitOverlayLayer();
  }, [modalOpen]);

  return (
    <div className={styles.root}>
      <div className={styles.searchBox}>
        <input
          className={styles.searchInput}
          name="Code"
          value={codeValue}
          placeholder="Enter your Fast Courier Order Number"
          disabled={loading}
          onChange={(e) => setCodeValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !loading) runLookup(codeValue.trim());
          }}
        />
        <button
          type="button"
          className={styles.searchButton}
          disabled={loading}
          onClick={() => runLookup(codeValue.trim())}
        >
          {loading ? <span className={styles.buttonSpinner} aria-hidden="true" /> : 'Track Now'}
        </button>
      </div>

      {/* Results open in a popup so the embedded form's height never changes. */}
      <Modal
        show={modalOpen}
        onHide={() => setShowResults(false)}
        container={getOverlayContainer}
        scrollable
        size="lg"
        dialogClassName={styles.modalDialog}
        aria-labelledby="fc-tracking-results-title"
      >
        <Modal.Header closeButton className={styles.modalHeader}>
          <Modal.Title id="fc-tracking-results-title">
            {status === 'error'
              ? "We couldn't find that order number..."
              : events.length === 0
              ? "Your order hasn't been picked up yet..."
              : 'Tracking details'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className={styles.modalBody}>
          {status === 'error' && (
            <div className={styles.stateMessage}>
              <div className={classNames(styles.stateIcon, styles.stateIconError)}>!</div>
              <p className={styles.stateText}>
                We couldn't find any details about the provided order number. Please check the Fast Courier email
                for your order number.
              </p>
            </div>
          )}

          {status === 'complete' && events.length === 0 && (
            <div className={styles.stateMessage}>
              <div className={classNames(styles.stateIcon, styles.stateIconInfo)}>⏱</div>
              <p className={styles.stateText}>
                We haven't received any updates from your selected courier yet. Please check back shortly.
              </p>
            </div>
          )}

          {status === 'complete' && events.length !== 0 && (
            <div className={styles.timelineWrap}>
              <ol className={styles.timeline}>
                {events.map((item, i) => (
                  <li className={classNames(styles.event, { [styles.eventCurrent]: i === 0 })} key={i}>
                    <span className={styles.eventDot} aria-hidden="true" />
                    <div className={styles.eventContent}>
                      <div className={styles.eventTop}>
                        <span className={styles.eventStatus}>{item.master_status ? item.master_status : 'Processing'}</span>
                        <span className={styles.eventCourier}>{item.courier}</span>
                      </div>
                      <div className={styles.eventTime}>{item.date} {item.time}</div>
                      <div className={styles.eventAction}>
                        {item.img && <img alt="Carrier" className={styles.eventLogo} src={item.img} />}
                        <span className={styles.eventActionText}>{item.action}</span>
                      </div>
                    </div>
                  </li>
                ))}
              </ol>

              <div className={styles.footer}>
                <div className={styles.footerTitle}>For additional tracking</div>
                <div className={styles.footerRow}>
                  Use tracking number <span className={styles.footerHighlight}>{consignmentCode}</span> on{' '}
                  <span className={styles.footerHighlight}>{courierName}</span> website:{' '}
                  <a
                    target="_blank"
                    className={styles.footerLink}
                    href={courierWebsite ? courierWebsite : ''}
                    rel="noreferrer"
                  >
                    {courierWebsite}
                  </a>
                </div>
                {packageIds.length > 0 && (
                  <div className={styles.footerRow}>
                    {packageIds.length > 1 ? 'Package IDs: ' : 'Package ID: '}
                    {packageIds.map((id, i) => (
                      <span key={i} className={styles.footerHighlight}>
                        {id}{i < packageIds.length - 1 ? ', ' : ''}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
}
